import React, { useId } from 'react'
import { T } from '@transifex/react'
import { FulfilmentType } from '../../../../models/fulfilment'

export interface FulfilmentSelectProps {
  title?: string
  onTypeChange: (type: FulfilmentType) => void
  defaultType?: string
  showUnspecified?: boolean
  message?: string
  disabled?: boolean
  stretched?: boolean
  outOfStockMessage?: string
  name?: string
  /**
   * When provided, restricts the selectable fulfilment types to this list.
   * Types not in the list remain visible but are disabled (greyed out).
   * Used e.g. for new items added while continuing a fulfilment sale, which
   * may only be the sale's existing type or None (Take Now).
   */
  allowedTypes?: FulfilmentType[]
}

const fulfilmentOptions = [
  {
    label: <T _str="None" _context="Fulfilment select" />,
    trackId: 'unspecified-fulfilment',
    type: FulfilmentType.UNSPECIFIED,
  },
  {
    label: <T _str="Delivery" _context="Fulfilment select" />,
    trackId: 'delivery-fulfilment',
    type: FulfilmentType.DELIVERY,
  },
  {
    label: <T _str="Pickup" _context="Fulfilment select" />,
    trackId: 'pickup-fulfilment',
    type: FulfilmentType.PICKUP,
  },
]

export const useFulfilmentSelectModel = ({
  defaultType,
  disabled,
  name,
  onTypeChange,
  showUnspecified,
  allowedTypes,
}: FulfilmentSelectProps) => {
  const fallbackFulfilmentTypeName = `fulfilType-${useId()}`
  const fulfilmentTypeName = name || fallbackFulfilmentTypeName
  const options = showUnspecified
    ? fulfilmentOptions
    : fulfilmentOptions.filter(
        option => option.type !== FulfilmentType.UNSPECIFIED
      )

  return {
    fulfilmentTypeName,
    getIsDisabled: (type: FulfilmentType) => {
      // When allowedTypes is provided, options not in the list stay visible but
      // are disabled (greyed out) rather than removed. Used e.g. for new items
      // added while continuing a fulfilment sale, where the other fulfilment
      // type is not selectable but should remain shown.
      if (allowedTypes && !allowedTypes.includes(type)) {
        return true
      }
      return defaultType !== type && disabled
    },
    handleChange: (value: string | undefined) => {
      if (value) {
        onTypeChange(value as FulfilmentType)
      }
    },
    options,
  }
}
